import React, { createContext, useContext } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import { Box, Typography, Link as MuiLink, Alert } from '@mui/material';
import { AppRouter } from '../router';
import { SubscriptionProvider } from '../contexts/SubscriptionContext';
import { SourcesProvider } from '../contexts/SourcesContext';
import '@aws-amplify/ui-react/styles.css';
import './auth-styles.css';
import { getSignupUrl } from '../aws.config';

// Auth context to pass signOut function
interface AuthContextType {
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthWrapper');
  }
  return context;
};

/**
 * Authentication wrapper component using AWS Amplify Authenticator
 * Wraps the application with authentication UI
 */
const AuthWrapper: React.FC = () => {
  const [showSuccessMessage, setShowSuccessMessage] = React.useState(false);

  // Check for signup success parameter on mount
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('signup_success') === 'true') {
      setShowSuccessMessage(true);
      
      // Clean up URL parameter
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('signup_success');
      window.history.replaceState({}, '', newUrl.toString());
      
      // Auto-hide message after 10 seconds
      setTimeout(() => setShowSuccessMessage(false), 10000);
    }
  }, []);

  return (
    <Box>
      {/* Success message banner */}
      {showSuccessMessage && (
        <Alert 
          severity="success" 
          onClose={() => setShowSuccessMessage(false)}
          sx={{ 
            position: 'fixed', 
            top: 20, 
            left: '50%', 
            transform: 'translateX(-50%)',
            zIndex: 9999,
            minWidth: '300px',
            maxWidth: '90vw'
          }}
        >
          Signup completed successfully! Please log in to Copilot.
        </Alert>
      )}
      
      <Authenticator
        // Customize the sign up fields
        signUpAttributes={['email']}
        // Hide the sign up tab - disable registration for now
        hideSignUp={true}
        // Custom styling for the authenticator
        components={{
        Header: () => (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              mb: 4,
              pt: 2,
            }}
          >
            <Box
              component="img"
              src={new URL('../assets/gekkovet-logo.png', import.meta.url).href}
              alt="Gekkovet Logo"
              sx={{ 
                height: 60,
                width: 'auto',
                objectFit: 'contain',
                mb: 2,
              }}
            />
          </Box>
        ),
        Footer: () => {
          const signupUrl = getSignupUrl();
          return (
            <Box 
              sx={{ 
                textAlign: 'center', 
                mt: 2, 
                p: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 1,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Don&apos;t have an account?{' '}
                <MuiLink 
                  href={signupUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  sx={{ 
                    fontWeight: 'medium',
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  Sign up for Gekkovet Copilot
                </MuiLink>
              </Typography>
            </Box>
          );
        },
      }}
      // Customize the form fields
      formFields={{
        signIn: {
          username: {
            placeholder: 'Enter your email address',
            isRequired: true,
            label: 'Email',
          },
          password: {
            placeholder: 'Enter your password',
            isRequired: true,
            label: 'Password',
          },
        },
      }}
    >
      {({ signOut }) => (
        <AuthContext.Provider value={{ signOut: signOut || (() => {}) }}>
          <SubscriptionProvider>
            <SourcesProvider>
              <AppRouter signOut={signOut || (() => {})} />
            </SourcesProvider>
          </SubscriptionProvider>
        </AuthContext.Provider>
      )}
    </Authenticator>
    </Box>
  );
};

export default AuthWrapper;
